/* eslint-disable max-lines-per-function */
import React from "react";
import { useField } from "formik";
import { Grid, Typography } from "@mui/material";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import { Close, Search } from "@mui/icons-material";

const ITEM_HEIGHT = 48;
const cardContentStyle = { maxHeight: ITEM_HEIGHT * 6.5, overflow: "auto" } as const;


type Option = {
  label: string;
  id: string;
}

type Props = {
  name: string;
  options: Option[];
  selectedOptions: Option[];
  label?: string;
  height?: number;
}
type OptionMap = Record<string, Option>

const idMapFromList = (list: Option[]) =>
  list.reduce((acc, option) => ({ ...acc, [option.id]: option }), {});

const removeObjectWithId = (arr: Option[], id: string) => {
  // Making a copy with the Array from() method
  const arrCopy = Array.from(arr);

  const objWithIdIndex = arrCopy.findIndex((obj) => obj.id === id);
  arrCopy.splice(objWithIdIndex, 1);
  return arrCopy;
};
const NoItems = () => <Grid style={{ textAlign: "center" }}>
  <span style={{ alignSelf: "center", justifySelf: "center", textAlign: "center" }}>No Items...</span>
</Grid>;

export const MultiSelect: React.FC<Props> = (props) => {
  const [filter, setFilter] = React.useState("");
  const [filteredOptions, setFilteredOptions] = React.useState(props.options);
  const [filteredOptionsMap, setFilteredOptionsMap] =
    React.useState<OptionMap>(idMapFromList(props.options));
  const [valueMap, setValueMap] = React.useState<OptionMap>({});
  const [field, , helpers] = useField<Option[]>(props);
  const { value } = field;
  const { setValue: setFormikValue, setTouched } = helpers;
  const setValue = (newValue: Option[]) => {
    setTouched(true);
    setFormikValue(newValue);
  };
  React.useEffect(() => {
    const filterVal = filter.toLowerCase();
    const newFilteredOptions = props.options
      .filter(({ label }) => label.toLowerCase().includes(filterVal));
    setFilteredOptions(newFilteredOptions);
    setFilteredOptionsMap(idMapFromList(newFilteredOptions));
  }, [filter]);
  React.useEffect(() => setValueMap(idMapFromList(value)), [value]);

  const allFilteredChecked = (!!value.length && filteredOptions.every(({ id }) => id in valueMap));


  const setAllValues =
  (values: Option[], valuesMap: OptionMap, optionsMap: OptionMap, checked: boolean) => {
    let newValues = [] as Option[];
    if (checked) {
      newValues = values.filter(({ id }) => (!(id in optionsMap)));
    } else {
      newValues = Object.values({ ...valuesMap, ...optionsMap });
    }
    setValue(newValues);
  };
  return <Grid container>
    {props.label && <Grid size={{ md: 12 }}>
      <Typography variant="h6">
        {props.label}
      </Typography>
    </Grid>}
    <Grid container spacing={0} alignItems="stretch" size={{ xs: 12, md: 12 }}>
      <Grid size={{ md: 6 }}>
        <Card style={{ height: "100%" }} variant="outlined" square>
          <CardHeader title={<TextField value={filter} onChange={(event) => {
            setFilter(event.currentTarget.value);
          }} placeholder="Search..." fullWidth />} action={<IconButton><Search /></IconButton>} />
          <CardContent style={cardContentStyle}>
            <MenuList dense>
              <MenuItem
                onClick={() =>
                  setAllValues(value, valueMap, filteredOptionsMap, allFilteredChecked)}
                disabled={filteredOptions.length === 0}>
                <ListItemIcon>
                  <Checkbox
                    checked={allFilteredChecked}
                    style={{ padding: 0 }}
                    indeterminate={value.length > 0 && !allFilteredChecked}
                  />
                </ListItemIcon>
                <ListItemText>
            Select All
                </ListItemText>
              </MenuItem>

              {filteredOptions.map((option: Option) => {
                const exists = value && value.findIndex((obj: Option) => obj.id === option.id) > -1;
                return (
                  <MenuItem
                    key={`option-${option.id}`}
                    onClick={() => {
                      setValue(exists ? (
                        removeObjectWithId(value, option.id)
                      ) : (
                        [option, ...(value || [])]
                      ));
                    }}>
                    <ListItemIcon>
                      <Checkbox size="small" checked={exists}/>
                    </ListItemIcon>
                    <ListItemText>
                      {option.label}
                    </ListItemText>
                  </MenuItem>
                );
              })}

            </MenuList>
            {!filteredOptions.length && (<NoItems />)}
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ md: 6 }}>
        <Card style={{ height: "100%" }} variant="outlined" square>
          <CardHeader titleTypographyProps={{ variant: "body1" }} title={value && value.length ? (<>
            {value.length} selected <a href="#" onClick={(evt) => {
              evt.preventDefault();
              setValue([]);
            }} style={{ float: "right" }}
            >Clear All</a>
          </>) : (<>None Selected</>)} />
          <CardContent style={cardContentStyle}>
            {!!(value && value.length) && (
              <MenuList dense>
                {(value.map(({ label, id }: Option) => (
                  <MenuItem
                    key={`label-${id}`}
                    onClick={() => {
                      setValue(removeObjectWithId(value, id));
                    }}>
                    <ListItemText>
                      {label}
                    </ListItemText>
                    <Close />
                  </MenuItem>
                )))}
              </MenuList>)}
            {value && !value.length && (<NoItems />)}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  </Grid>;
};
